import { useEffect, useMemo, useState } from "react";
import { Award, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { web, errMsg, toList, avatarSrc } from "../../lib/api";
import { useApi } from "../../hooks/useApi";
import { useCWeek, thisMonday } from "../../hooks/useCWeek";
import { levelOf, useAuth } from "../../context/AuthContext";
import { SpotlightShare } from "./SpotlightShare";
import { Btn, Avatar, shortDate } from "./kit";
import { Modal, Field, Chips } from "./Form";
import { AwardWall } from "./AwardWall";

const AwardForm = ({ open, onClose, tid, level, items, current, onSaved }) => {
  const roster = useApi(open && tid && `/teams/${tid}/players`);
  const all = useMemo(() => toList(roster.data, ["players"]), [roster.data]);
  const inLevel = all.filter((p) => levelOf(p) === level);
  const players = inLevel.length ? inLevel : all;
  const [pid, setPid] = useState("");
  const [c, setC] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setPid(current?.player_id ?? ""); setC(current?.c_name ?? ""); } }, [open, current]);
  const save = async () => {
    if (!pid || !c) return toast.error("Pick a player and a C");
    setBusy(true);
    try {
      await web.post(`/teams/${tid}/c-of-week`, { level, week: thisMonday(), player_id: pid, c_name: c });
      toast.success(current?.player_id === pid ? "Award updated" : "Award saved. The player and parents get an email");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} testid="c-week-form" title="7 C's Player of the Week" description={`Week of ${shortDate(thisMonday())}. One player per week.`}>
      <div className="mt-2 space-y-4">
        <Field label="Player">
          <select data-testid="c-week-player-select" value={pid} onChange={(e) => setPid(e.target.value)}
            className="w-full rounded-xl border border-[#1E293B] bg-[#0F172A] px-4 py-3 text-white outline-none focus:border-[#39FF14]">
            <option value="">{roster.loading ? "Loading…" : "Choose a player"}</option>
            {players.map((p) => <option key={p.id} value={p.id}>{p.jersey ? `#${p.jersey} ` : ""}{p.name}</option>)}
          </select>
          {!roster.loading && all.length > 0 && !inLevel.length && <p data-testid="c-week-no-level-players" className="mt-2 text-xs text-slate-400">No {level === "jv" ? "JV" : "Varsity"} players on the roster yet, so the whole roster is shown.</p>}
        </Field>
        <Field label="Which C did they show?"><Chips testid="c-week-c" options={items.map((i) => [i.name, i.name])} value={c} onChange={setC} /></Field>
        <div className="flex justify-end gap-2 pt-2">
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn data-testid="c-week-save" onClick={save} disabled={busy}>{busy ? "Saving…" : "Award"}</Btn>
        </div>
      </div>
    </Modal>
  );
};

const Winner = ({ tid, a, cw, items }) => {
  const text = items.find((i) => i.name === a.c_name)?.text;
  return (
    <Link to={`/coach/players/${a.player_id}`} data-testid="c-week-winner" className="flex min-w-0 flex-1 items-center gap-4 hover:opacity-90">
      <Avatar name={a.player_name} src={avatarSrc(tid, { id: a.player_id, has_app_photo: true })} size="h-14 w-14" />
      <div className="min-w-0">
        <p className="truncate font-display text-2xl font-bold uppercase leading-none text-white">{a.player_name}</p>
        <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#39FF14] px-2.5 py-0.5 font-mono-gd text-xs font-bold uppercase tracking-wider text-[#0A0E1A]"><Award className="h-3 w-3" />{a.c_name}</p>
        {text && <p className="mt-2 text-sm text-slate-300">{text}</p>}
        <p className="mt-1 font-mono-gd text-[11px] uppercase tracking-wider text-slate-400">{cw.counts[a.player_id] ?? 1}× C of the Week</p>
      </div>
    </Link>
  );
};

/** This week's 7 C's Player of the Week for a level, recent winners, and the coach's award button. */
export const CWeekPanel = ({ tid, level, canEdit, items, closing }) => {
  const { membership } = useAuth();
  const cw = useCWeek(tid);
  const [open, setOpen] = useState(false);
  const cur = cw.current[level];
  const undo = async () => {
    if (!window.confirm(`Remove ${cur.player_name}'s award for this week?`)) return;
    try { await web.delete(`/teams/${tid}/c-of-week/${cur.id}`); toast.success("Award removed"); cw.reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="c-week-panel" className="relative mt-6 rounded-2xl border border-[#39FF14]/40 bg-[#0A0E1A] p-5">
      <div className="flex flex-wrap items-center gap-3">
        <p className="flex-1 font-mono-gd text-xs uppercase tracking-[0.25em] text-[#39FF14]">7 C's Player of the Week · {shortDate(thisMonday())}</p>
        {cur && (canEdit || membership?.player_id === cur.player_id) && <SpotlightShare tid={tid} award={cur} text={items.find((i) => i.name === cur.c_name)?.text} teamName={membership?.team_name ?? ""} level={level} closing={closing} />}
        {canEdit && <Btn variant="ghost" data-testid="c-week-award-button" onClick={() => setOpen(true)}><Award className="h-4 w-4" />{cur ? "Change" : "Award this week"}</Btn>}
        {canEdit && cur && <button data-testid="c-week-remove" onClick={undo} aria-label="Remove award" className="rounded-full p-2 text-slate-400 hover:text-red-200"><Trash2 className="h-4 w-4" /></button>}
      </div>
      <div className="mt-4">
        {cur ? <Winner tid={tid} a={cur} cw={cw} items={items} /> : <p data-testid="c-week-empty" className="text-sm text-slate-300">{canEdit ? "No one picked yet this week. Award the player who best lived out a C." : "This week's winner hasn't been picked yet."}</p>}
      </div>
      <AwardWall tid={tid} level={level} items={items} season={cw.season} />
      {canEdit && <AwardForm open={open} onClose={() => setOpen(false)} tid={tid} level={level} items={items} current={cur} onSaved={cw.reload} />}
    </div>
  );
};
